import React, {useEffect, useState} from "react"
import {performWithApiCallErrorHandling} from "../../services/api/api-utils"
import {useServices} from "../../services/services"
import {ImprovementScore} from "./improvements/improvement-score"
import {TransactionImpact} from "../dashboard/transactions/transaction-amount/transaction-amount"
import {Message} from "../../components-shared/message/message"
import {HeadingMedium} from "../../components-shared/heading/heading"
import {Button} from "../../components-shared/button/button"
import {WithVerticalSpacing} from "../../components-shared/spacing/with-vertical-spacing"
import {Amount} from "../../components-shared/formatted-number/formatted-number"
import {CommuteByBikeForm, CommuteByBikeImprovementScore, CommuteByBikeImprovementScoreItem} from "./improvements/commute-by-bike"
import {SolarPanelsForm, SolarPanelsImprovementScore, SolarPanelsImprovementScoreItem} from "./improvements/solar-panels"
import {SecondHandClothesForm, SecondHandClothesImprovementScore, SecondHandClothesImprovementScoreItem} from "./improvements/second-hand-clothes"
import {FuelType} from "../../domain/fuel-type"
import {gaSendClimateAdviceEvent} from "../../services/analytics/google-analytics-helper"

export const ClimateAdvice: React.FC = () => {
  const services = useServices()
  const [error, setError] = useState<string | undefined>(undefined)
  const [improvementScoreItems, setImprovementScoreItems] = useState<SpecificImprovementScoreItem[] | null>(null)
  const [selectedImprovementScoreItem, setSelectedImprovementScoreItem] = useState<SpecificImprovementScoreItem | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    if (services === null) return

    // noinspection JSIgnoredPromiseFromCall
    performWithApiCallErrorHandling(async () => {
      const improvementScores = await services.improvementsApi.getInitialImprovementScores(controller.signal)
      setImprovementScoreItems(improvementScores.map(x => convertToImprovementScoreItem(x)))
    }, "Er is iets misgegaan tijdens het ophalen van de klimaatadviesgegevens.", setError)

    return () => controller.abort()
  }, [services])

  if (error) {
    return <Message type={"error"}>{error}</Message>
  }

  const updateSelectedImprovementScoreItem = (newImprovementScoreItem: SpecificImprovementScoreItem) => {
    if (improvementScoreItems === null || selectedImprovementScoreItem === null) return
    const index = improvementScoreItems.indexOf(selectedImprovementScoreItem)
    if (index === -1) return
    improvementScoreItems[index] = newImprovementScoreItem
    setImprovementScoreItems([...improvementScoreItems])
    setSelectedImprovementScoreItem(newImprovementScoreItem)
  }

  const hasImprovementScores = improvementScoreItems !== null && improvementScoreItems.length > 0
  const hasNoImprovementScores = improvementScoreItems !== null && improvementScoreItems.length === 0

  const clickImprovement = (improvementScoreItem: SpecificImprovementScoreItem, title: string) => {
    setSelectedImprovementScoreItem(improvementScoreItem)
    gaSendClimateAdviceEvent("Open improvement " + title)
  }

  return <>
    {hasImprovementScores && <WithVerticalSpacing>
      <div>
        <table>
          <thead>
            <tr>
              <td>Actie</td>
              <td>CO<sub>2</sub>-reductie per jaar</td>
              <td>CO<sub>2</sub>-investering</td>
              <td>Besparing per jaar</td>
              <td>Investering</td>
              <td>&nbsp;</td>
            </tr>
          </thead>
          <tbody>
            {improvementScoreItems && improvementScoreItems.map(improvementScoreItem => <tr key={improvementScoreItem.improvementScore.improvement}>
              <td><Button onClick={() => clickImprovement(improvementScoreItem, improvementScoreItem.title)}>{improvementScoreItem.title}</Button></td>
              <td><TransactionImpact impact={improvementScoreItem.improvementScore.co2ReductionPerYear} decimals={0}/></td>
              <td><TransactionImpact impact={improvementScoreItem.improvementScore.co2Investment} decimals={0}/></td>
              <td><Amount amount={improvementScoreItem.improvementScore.financialReductionPerYear} decimals={0}/></td>
              <td><Amount amount={improvementScoreItem.improvementScore.financialInvestment} decimals={0}/></td>
              <td>{improvementScoreItem.explanation}</td>
            </tr>)}
          </tbody>
        </table>
      </div>

      {selectedImprovementScoreItem && <ImprovementScoreDetails improvementScoreItem={selectedImprovementScoreItem} updateImprovementScoreItem={updateSelectedImprovementScoreItem} />}
    </WithVerticalSpacing>}

    {hasNoImprovementScores && <Message type="alert">Er zijn nog geen verbeteringen gedetecteerd. Het kan zijn dat er nog geen transacties zijn ingevoerd.</Message>}
  </>
}

export interface ImprovementScoreDetailsProps {
  improvementScoreItem: SpecificImprovementScoreItem
  updateImprovementScoreItem: (newImprovementScoreItem: SpecificImprovementScoreItem) => void
}

export const ImprovementScoreDetails: React.FC<ImprovementScoreDetailsProps> = ({improvementScoreItem, updateImprovementScoreItem}) => {
  return <div>
    <HeadingMedium>{improvementScoreItem.title}</HeadingMedium>
    <ImprovementScoreForm improvementScoreItem={improvementScoreItem} updateImprovementScoreItem={updateImprovementScoreItem}/>
  </div>
}

export interface ImprovementScoreFormProps {
  improvementScoreItem: SpecificImprovementScoreItem
  updateImprovementScoreItem: (newImprovementScoreItem: SpecificImprovementScoreItem) => void
}

const ImprovementScoreForm: React.FC<ImprovementScoreFormProps> = ({improvementScoreItem, updateImprovementScoreItem}) => {
  switch (improvementScoreItem.improvementScore.improvement) {
    case "CommuteByBike": return <CommuteByBikeForm improvementScoreItem={improvementScoreItem as CommuteByBikeImprovementScoreItem} updateImprovementScoreItem={updateImprovementScoreItem}/>
    case "SecondHandClothes": return <SecondHandClothesForm improvementScoreItem={improvementScoreItem as SecondHandClothesImprovementScoreItem} updateImprovementScoreItem={updateImprovementScoreItem}/>
    case "SolarPanels": return <SolarPanelsForm improvementScoreItem={improvementScoreItem as SolarPanelsImprovementScoreItem} updateImprovementScoreItem={updateImprovementScoreItem}/>
  }
}




export type SpecificImprovementScoreItem = CommuteByBikeImprovementScoreItem | SecondHandClothesImprovementScoreItem | SolarPanelsImprovementScoreItem

function convertToImprovementScoreItem<T extends ImprovementScore>(improvementScore: T): SpecificImprovementScoreItem {
  switch (improvementScore.improvement) {
    case "CommuteByBike": return new CommuteByBikeImprovementScoreItem(improvementScore as unknown as CommuteByBikeImprovementScore)
    case "SecondHandClothes": return new SecondHandClothesImprovementScoreItem(improvementScore as unknown as SecondHandClothesImprovementScore)
    case "SolarPanels": return new SolarPanelsImprovementScoreItem(improvementScore as unknown as SolarPanelsImprovementScore)
  }
}

export type ImprovementParameter = NumberImprovementParameter | PercentageImprovementParameter | FuelTypeImprovementParameter

export interface IImprovementParameter {
  key: string
  description: string
}

export class NumberImprovementParameter implements IImprovementParameter {
  constructor(
    public key: string,
    public value: number,
    public description: string) {

  }
}

export class PercentageImprovementParameter implements IImprovementParameter {
  constructor(
    public key: string,
    public factor: number,
    public description: string) {
  }
}

export class FuelTypeImprovementParameter implements IImprovementParameter {
  constructor(
    public key: string,
    public value: FuelType,
    public description: string) {
  }
}